import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, AlertTriangle, Clock, CheckCircle2, XCircle, RefreshCw,
  Download, Filter, ChevronDown, Eye, Trash2, Lock, Unlock, Search,
  Bell, AlertOctagon, Info, X, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  success:          { label: 'Éxito',              icon: CheckCircle2,  cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25' },
  error:            { label: 'Error',              icon: XCircle,       cls: 'text-red-400 bg-red-500/10 border-red-500/25' },
  pending_approval: { label: 'Aprobación pend.',   icon: Clock,         cls: 'text-amber-400 bg-amber-500/10 border-amber-500/25' },
};

const SEV_CFG = {
  critical: { label: 'Crítica', cls: 'text-red-400 bg-red-500/10 border-red-500/25',    icon: AlertOctagon },
  warning:  { label: 'Aviso',   cls: 'text-amber-400 bg-amber-500/10 border-amber-500/25', icon: AlertTriangle },
  info:     { label: 'Info',    cls: 'text-blue-400 bg-blue-500/10 border-blue-500/25',  icon: Info },
};

const fmtTs = (iso) => {
  try {
    return new Date(iso).toLocaleString('es-PE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false });
  } catch { return iso?.slice(0,19) || '—'; }
};

// ── BADGE ─────────────────────────────────────────────────────────────────────
const Badge = ({ text, cls }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cls}`}>{text}</span>
);

export default function AdminSecurityAuditPage({ authFetch }) {
  const [activeSection, setActiveSection] = useState('audit');
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState({});
  const [alerts, setAlerts] = useState({ live_alerts: [], history: [], total_live: 0, critical_count: 0 });
  const [authLinks, setAuthLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // filters
  const [filterAgent, setFilterAgent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchAction, setSearchAction] = useState('');

  // revocation modal
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeStep, setRevokeStep] = useState(1); // 1 = confirm, 2 = done
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeLoading, setRevokeLoading] = useState(false);

  // ── loaders ────────────────────────────────────────────────────────────────
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAgent)    params.set('agent', filterAgent);
      if (filterStatus)   params.set('status', filterStatus);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo)   params.set('date_to', filterDateTo);
      if (searchAction)   params.set('action_type', searchAction);
      const r = await authFetch(`/api/security/audit-logs?${params}`);
      const d = await r.json();
      setLogs(d.logs || []);
      setLogStats(d.stats || {});
    } catch { /* keep stale */ } finally { setLoading(false); }
  }, [authFetch, filterAgent, filterStatus, filterDateFrom, filterDateTo, searchAction]);

  const loadAlerts = useCallback(async () => {
    try {
      const r = await authFetch('/api/security/alerts');
      const d = await r.json();
      setAlerts(d);
    } catch { /* keep */ }
  }, [authFetch]);

  const loadLinks = useCallback(async () => {
    try {
      const r = await authFetch('/api/security/auth-links');
      const d = await r.json();
      setAuthLinks(d.links || []);
    } catch { /* keep */ }
  }, [authFetch]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { if (activeSection === 'alerts') loadAlerts(); }, [activeSection, loadAlerts]);
  useEffect(() => { if (activeSection === 'revoke') loadLinks(); }, [activeSection, loadLinks]);

  // ── export CSV ─────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAgent)    params.set('agent', filterAgent);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo)   params.set('date_to', filterDateTo);
      const r = await authFetch(`/api/security/audit-logs/export?${params}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `audit_logs_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* show error */ } finally { setExportLoading(false); }
  };

  // ── dismiss alert ──────────────────────────────────────────────────────────
  const handleDismiss = async (alert_id) => {
    await authFetch(`/api/security/alerts/${alert_id}/dismiss`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dismissed_by: '' }),
    });
    loadAlerts();
  };

  // ── revoke flow ────────────────────────────────────────────────────────────
  const openRevoke = (link) => { setRevokeTarget(link); setRevokeStep(1); setRevokeReason(''); };
  const closeRevoke = () => { setRevokeTarget(null); setRevokeStep(1); };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeLoading(true);
    try {
      const r = await authFetch(`/api/security/auth-links/${revokeTarget.id}/revoke`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: revokeReason || 'Revocación manual desde panel', confirmation_token: revokeTarget.id }),
      });
      const d = await r.json();
      if (d.status === 'revoked') { setRevokeStep(2); loadLinks(); }
    } catch { /* error */ } finally { setRevokeLoading(false); }
  };

  // ── unique agents for filter ───────────────────────────────────────────────
  const uniqueAgents = [...new Set(logs.map(l => l.agent).filter(Boolean))];

  const SECTIONS = [
    { id: 'audit',  label: 'Log de Auditoría',       icon: ShieldCheck },
    { id: 'alerts', label: 'Alertas de Seguridad',   icon: Bell,       badge: alerts.critical_count > 0 ? alerts.critical_count : null },
    { id: 'revoke', label: 'Gestión Auth Links',     icon: Lock },
  ];

  return (
    <div className="space-y-6" data-testid="security-audit-panel">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-black text-white">Centro de Auditoría y Seguridad</h2>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/25">EN DESARROLLO</span>
          </div>
          <p className="text-slate-500 text-sm">Log de acciones de agentes · Alertas de seguridad · Revocación de accesos</p>
        </div>
        <button onClick={() => { loadLogs(); loadAlerts(); loadLinks(); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs text-slate-400 border border-white/[0.07] hover:border-primary/30 hover:text-white transition-all">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {/* ── Section nav ── */}
      <div className="flex gap-2" data-testid="security-section-nav">
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
              activeSection === s.id
                ? 'bg-gradient-to-r from-primary to-cyan-400 text-[#081428] border-transparent shadow-[0_0_12px_rgba(0,229,255,0.2)]'
                : 'bg-white/[0.03] text-slate-400 border-white/[0.06] hover:text-white hover:border-primary/20'
            }`} data-testid={`security-nav-${s.id}`}>
            <s.icon className="w-4 h-4" /> {s.label}
            {s.badge && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center">
                {s.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══ SECTION: AUDIT LOG ══ */}
      {activeSection === 'audit' && (
        <div className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3" data-testid="audit-stats">
            {[
              { label: 'Total', value: logStats.total || 0,   cls: 'from-primary to-cyan-400', bg: 'text-cyan-300 bg-cyan-500/10' },
              { label: 'Éxito', value: logStats.success || 0, cls: 'from-emerald-500 to-teal-400', bg: 'text-emerald-300 bg-emerald-500/10' },
              { label: 'Error', value: logStats.error || 0,   cls: 'from-red-500 to-rose-400', bg: 'text-red-300 bg-red-500/10' },
              { label: 'Pendiente', value: logStats.pending || 0, cls: 'from-amber-500 to-yellow-400', bg: 'text-amber-300 bg-amber-500/10' },
            ].map(s => (
              <div key={s.label} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0b1a35]/80 p-4">
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${s.cls}`} />
                <p className="text-3xl font-black text-white">{s.value}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="glass-card p-4 flex flex-wrap gap-3 items-end" data-testid="audit-filters">
            <div>
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-widest">Agente</p>
              <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
                className="bg-[#0b1a35] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary/40"
                data-testid="filter-agent">
                <option value="">Todos</option>
                {uniqueAgents.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-widest">Estado</p>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="bg-[#0b1a35] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary/40"
                data-testid="filter-status">
                <option value="">Todos</option>
                <option value="success">Éxito</option>
                <option value="error">Error</option>
                <option value="pending_approval">Pendiente</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-widest">Desde</p>
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                className="bg-[#0b1a35] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary/40"
                data-testid="filter-date-from" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-widest">Hasta</p>
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                className="bg-[#0b1a35] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary/40"
                data-testid="filter-date-to" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-widest">Acción</p>
              <input type="text" placeholder="Buscar acción..." value={searchAction} onChange={e => setSearchAction(e.target.value)}
                className="bg-[#0b1a35] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary/40 w-36"
                data-testid="filter-action" />
            </div>
            <div className="flex gap-2 ml-auto">
              <button onClick={() => { setFilterAgent(''); setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo(''); setSearchAction(''); }}
                className="px-3 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] hover:text-white transition-all" data-testid="clear-filters">
                Limpiar
              </button>
              <button onClick={loadLogs}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary/15 text-primary border border-primary/25 hover:bg-primary/20 transition-all" data-testid="apply-filters">
                <Filter className="w-3 h-3 inline mr-1" />Aplicar
              </button>
              <button onClick={handleExport} disabled={exportLoading}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/15 transition-all" data-testid="export-csv-btn">
                <Download className={`w-3 h-3 inline mr-1 ${exportLoading ? 'animate-bounce' : ''}`} />CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-white/[0.06] overflow-hidden" data-testid="audit-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0b1a35]/90 border-b border-white/[0.06]">
                  {['Timestamp','Agente','Acción','Herramienta','Permisos','Resultado','Estado'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] text-slate-500 uppercase tracking-widest font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-600">
                    <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Cargando logs...
                  </td></tr>
                )}
                {!loading && logs.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-slate-600">Sin registros con los filtros actuales</td></tr>
                )}
                {!loading && logs.map((log, i) => {
                  const sc = STATUS_CFG[log.status] || STATUS_CFG.success;
                  const SIcon = sc.icon;
                  return (
                    <tr key={log.log_id || i}
                      className="border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors"
                      data-testid={`audit-row-${i}`}>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono whitespace-nowrap">{fmtTs(log.timestamp)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary text-xs font-mono font-semibold">{log.agent}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-300 font-mono">{log.action}</td>
                      <td className="px-4 py-3 text-xs text-violet-300 font-mono">{log.tool_used}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(log.permissions_applied || []).map(p => (
                            <span key={p} className="px-1.5 py-0.5 rounded-md bg-slate-700/50 text-slate-400 text-[10px] font-mono">{p}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 max-w-[180px] truncate" title={log.result}>{log.result}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${sc.cls}`}>
                          <SIcon className="w-2.5 h-2.5" />{sc.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ SECTION: ALERTS ══ */}
      {activeSection === 'alerts' && (
        <div className="space-y-4">
          {/* Live alerts header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
              <span className="text-white font-bold">{alerts.total_live} alertas activas</span>
              {alerts.critical_count > 0 && (
                <Badge text={`${alerts.critical_count} críticas`} cls="text-red-400 bg-red-500/10 border-red-500/25" />
              )}
            </div>
            <button onClick={loadAlerts} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs text-slate-400 border border-white/[0.07] hover:border-primary/30 hover:text-white transition-all">
              <RefreshCw className="w-3.5 h-3.5" />Actualizar
            </button>
          </div>

          {/* Live alerts */}
          {alerts.live_alerts.length === 0 ? (
            <div className="glass-card p-8 text-center text-slate-600" data-testid="no-alerts">
              <ShieldCheck className="w-10 h-10 text-emerald-500/50 mx-auto mb-3" />
              <p className="font-semibold text-slate-400">Sin alertas activas</p>
              <p className="text-sm mt-1">Todos los sistemas operan dentro del umbral</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="alerts-list">
              {alerts.live_alerts.map(alert => {
                const sc = SEV_CFG[alert.severity] || SEV_CFG.info;
                const SIcon = sc.icon;
                return (
                  <motion.div key={alert.alert_id} initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }}
                    className={`glass-card p-4 flex items-start gap-4 border ${alert.severity === 'critical' ? 'border-red-500/20' : 'border-amber-500/15'}`}
                    data-testid={`alert-${alert.type}`}>
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${sc.cls.split(' ').slice(1).join(' ')}`}>
                      <SIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-white text-sm">{alert.title}</p>
                        <Badge text={sc.label} cls={sc.cls} />
                        {alert.type === 'auth_link_expiry' && (
                          <Badge text="Auth Link" cls="text-violet-400 bg-violet-500/10 border-violet-500/25" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{alert.description}</p>
                      <p className="text-[11px] text-slate-600 mt-1">{fmtTs(alert.timestamp)}</p>
                    </div>
                    <button onClick={() => handleDismiss(alert.alert_id)}
                      className="shrink-0 p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Descartar"
                      data-testid={`dismiss-${alert.alert_id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* History */}
          {alerts.history.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3 mt-6">Historial descartado</p>
              <div className="space-y-2">
                {alerts.history.map(h => (
                  <div key={h.alert_id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500/50 shrink-0" />
                    <span className="text-xs text-slate-500 flex-1">{h.alert_id}</span>
                    <span className="text-[10px] text-slate-600">Descartado por {h.dismissed_by || '—'} · {fmtTs(h.dismissed_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ SECTION: AUTH LINKS ══ */}
      {activeSection === 'revoke' && (
        <div className="space-y-4">
          <p className="text-slate-400 text-sm">Gestiona y revoca el acceso granular de Auth Links activos. La revocación requiere doble confirmación y queda registrada en el log de auditoría.</p>

          <div className="rounded-2xl border border-white/[0.06] overflow-hidden" data-testid="auth-links-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0b1a35]/90 border-b border-white/[0.06]">
                  {['Cliente / Email','Proveedores','Estado','Expira en','Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] text-slate-500 uppercase tracking-widest font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {authLinks.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-600">Sin Auth Links</td></tr>
                )}
                {authLinks.map((link, i) => (
                  <tr key={link.id || i} className="border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors" data-testid={`auth-link-row-${i}`}>
                    <td className="px-4 py-3">
                      <p className="text-white text-xs font-semibold truncate max-w-[180px]">{link.client_email || '—'}</p>
                      <p className="text-slate-600 text-[10px] font-mono mt-0.5">{(link.id || '').slice(0,12)}…</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(link.providers || []).map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded-md bg-violet-500/10 text-violet-300 text-[10px] font-mono">{p}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {link.status === 'revoked'
                        ? <Badge text="Revocado" cls="text-red-400 bg-red-500/10 border-red-500/25" />
                        : link.status === 'used'
                          ? <Badge text="Usado" cls="text-emerald-400 bg-emerald-500/10 border-emerald-500/25" />
                          : link.status === 'expired'
                            ? <Badge text="Expirado" cls="text-slate-400 bg-slate-500/10 border-slate-500/25" />
                            : <Badge text="Pendiente" cls="text-amber-400 bg-amber-500/10 border-amber-500/25" />
                      }
                      {link.is_near_expiry && <Badge text="⚠ < 48h" cls="text-red-400 bg-red-500/10 border-red-500/25 ml-1" />}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-400">
                      {link.hours_left != null
                        ? link.hours_left <= 0 ? 'Expirado' : `${link.hours_left}h`
                        : '—'
                      }
                    </td>
                    <td className="px-4 py-3">
                      {link.status !== 'revoked' ? (
                        <button onClick={() => openRevoke(link)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-red-400 border border-red-500/25 hover:bg-red-500/10 transition-all"
                          data-testid={`revoke-btn-${i}`}>
                          <Lock className="w-3 h-3" />Revocar
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-600 font-mono">
                          {link.revoked_by && `Por: ${link.revoked_by}`}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── REVOCATION MODAL ── */}
      <AnimatePresence>
        {revokeTarget && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={e => { if (e.target === e.currentTarget) closeRevoke(); }}
            data-testid="revoke-modal">
            <motion.div initial={{ scale:0.95, y:12 }} animate={{ scale:1, y:0 }} exit={{ scale:0.95, y:8 }}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0d1f3c] p-6 shadow-2xl">
              {revokeStep === 1 ? (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
                      <AlertOctagon className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <h3 className="font-black text-white">Confirmar Revocación</h3>
                      <p className="text-xs text-slate-500">Paso 1 de 2 — esta acción no se puede deshacer</p>
                    </div>
                  </div>
                  <div className="glass-card p-4 mb-4 space-y-2">
                    <p className="text-xs text-slate-400"><span className="text-slate-600">Cliente:</span> <span className="text-white font-semibold">{revokeTarget.client_email}</span></p>
                    <p className="text-xs text-slate-400"><span className="text-slate-600">Herramientas:</span> {(revokeTarget.providers || []).join(', ')}</p>
                    <p className="text-xs text-slate-400"><span className="text-slate-600">Estado actual:</span> {revokeTarget.status}</p>
                  </div>
                  <div className="mb-5">
                    <label className="text-xs text-slate-500 block mb-1.5">Motivo de revocación</label>
                    <input
                      type="text"
                      value={revokeReason}
                      onChange={e => setRevokeReason(e.target.value)}
                      placeholder="Ej: Agente actúa fuera de umbral autorizado"
                      className="w-full bg-[#0b1a35] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-red-500/40"
                      data-testid="revoke-reason-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={closeRevoke} className="flex-1 px-4 py-2.5 rounded-xl text-sm text-slate-400 border border-white/[0.07] hover:text-white transition-all">Cancelar</button>
                    <button onClick={handleRevoke} disabled={revokeLoading}
                      className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-all disabled:opacity-50"
                      data-testid="revoke-confirm-btn">
                      {revokeLoading ? 'Revocando...' : 'Confirmar Revocación'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                    </div>
                    <h3 className="font-black text-white text-lg mb-2">Acceso Revocado</h3>
                    <p className="text-slate-400 text-sm mb-1">{revokeTarget.client_email}</p>
                    <p className="text-slate-600 text-xs">La acción quedó registrada en el log de auditoría</p>
                    <button onClick={closeRevoke} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-bold bg-primary/15 text-primary border border-primary/25 hover:bg-primary/20 transition-all" data-testid="revoke-done-btn">
                      Cerrar
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
